/**
 * The nine funnel templates. Data, not logic.
 *
 * ONE file on purpose. The split that produced it was a DATA/LOGIC split, not a
 * per-template one: nothing outside funnelConfig.ts imports any of these nine
 * by name — they exist to populate INDUSTRY_FUNNELS — so carving them into
 * "industry" and "demo" files would have bought three files where one is read,
 * which is the fragmentation the oversized-files plan warns against. The table
 * stays whole; it is long because the domain is long.
 *
 * The keywords are deliberately NOT translated: they match Thai campaign names
 * as they are actually typed. See funnelTypes.ts for why the LABELS are keys.
 */

import type { FunnelConfig } from './funnelTypes';

// ============================================================================
// DEFAULT FUNNEL (Industry-Agnostic)
// ============================================================================

/**
 * Default marketing funnel - works across all industries
 * Uses generic marketing terminology: Awareness → Consideration → Conversion
 */
export const DEFAULT_FUNNEL: FunnelConfig = {
  id: 'default',
  name: 'Standard Marketing Funnel',
  industry: 'general',
  stages: [
    {
      key: 'aware',
      label: 'Awareness',
      labelKey: 'funnel.default_aware_label',
      description: 'Brand awareness & reach campaigns',
      descriptionKey: 'funnel.default_aware_desc',
      keywords: [
        // English keywords
        'awareness',
        'brand',
        'reach',
        'visibility',
        'launch',
        'reveal',
        'showcase',
        'impression',
        // Campaign objective keywords
        'traffic',
        'video views',
        'brand awareness',
      ],
    },
    {
      key: 'consider',
      label: 'Consideration',
      labelKey: 'funnel.default_consider_label',
      description: 'Consideration & evaluation campaigns',
      descriptionKey: 'funnel.default_consider_desc',
      keywords: [
        // English keywords
        'consider',
        'consideration',
        'comparison',
        'compare',
        'learn',
        'guide',
        'review',
        'interest',
        'engagement',
        'research',
        'evaluation',
        'introduction',
        'discovery',
        // Product/feature related
        'features',
        'benefits',
        'tutorial',
        'how-to',
        'demo',
        'explainer',
        'information',
        'details',
        'specifications',
        'brochure',
      ],
    },
    {
      key: 'convert',
      label: 'Conversion',
      labelKey: 'funnel.default_convert_label',
      description: 'Conversion & acquisition campaigns',
      descriptionKey: 'funnel.default_convert_desc',
      keywords: [
        // English keywords
        'convert',
        'purchase',
        'buy',
        'shop now',
        'order',
        'retarget',
        'remarketing',
        'lead',
        'acquisition',
        'sale',
        'promotion',
        'offer',
        'deal',
        // Action-oriented
        'sign up',
        'register',
        'apply',
        'get started',
        'book',
        'call to action',
        'direct response',
        // Lead generation specific
        'quote',
        'request',
        'inquiry',
        'contact',
        'renewal',
        'discount',
      ],
    },
  ],
  metrics: {
    primary: 'conversions',
    secondary: 'leads',
  },
}

// ============================================================================
// INDUSTRY TEMPLATES
// ============================================================================

/**
 * Automotive industry funnel
 * Stages: Awareness → Consideration → Conversion (Bookings)
 */
export const AUTOMOTIVE_FUNNEL: FunnelConfig = {
  id: 'automotive',
  name: 'Automotive Funnel',
  industry: 'automotive',
  stages: [
    {
      key: 'aware',
      label: 'Awareness',
      labelKey: 'funnel.automotive_aware_label',
      description: 'Brand & model discovery',
      descriptionKey: 'funnel.automotive_aware_desc',
      keywords: [
        'launch',
        'reveal',
        'showroom',
        'model showcase',
        'brand awareness',
        'new model',
        'vehicle showcase',
      ],
    },
    {
      key: 'consider',
      label: 'Consideration',
      labelKey: 'funnel.automotive_consider_label',
      description: 'Research & comparison',
      descriptionKey: 'funnel.automotive_consider_desc',
      keywords: [
        'compare',
        'test drive',
        'features',
        'specifications',
        'review',
        'research',
        'learn more',
        'explore',
        'brochure',
        'configurator',
      ],
    },
    {
      key: 'convert',
      label: 'Conversion',
      labelKey: 'funnel.automotive_convert_label',
      description: 'Bookings & dealership visits',
      descriptionKey: 'funnel.automotive_convert_desc',
      keywords: [
        'book',
        'test drive',
        'quote',
        'dealer',
        'offer',
        'reserve',
        'order',
        'buy now',
        'financing',
        'incentive',
        'promotion',
      ],
    },
  ],
  metrics: {
    primary: 'bookings',
    secondary: 'test_drives',
  },
}

/**
 * E-commerce funnel
 * Stages: Awareness → Consideration → Purchase
 */
export const ECOMMERCE_FUNNEL: FunnelConfig = {
  id: 'ecommerce',
  name: 'E-commerce Funnel',
  industry: 'ecommerce',
  stages: [
    {
      key: 'aware',
      label: 'Awareness',
      labelKey: 'funnel.ecommerce_aware_label',
      description: 'Product discovery',
      descriptionKey: 'funnel.ecommerce_aware_desc',
      keywords: [
        'new arrival',
        'collection',
        'trending',
        'launch',
        'brand awareness',
        'product showcase',
      ],
    },
    {
      key: 'consider',
      label: 'Consideration',
      labelKey: 'funnel.ecommerce_consider_label',
      description: 'Product research',
      descriptionKey: 'funnel.ecommerce_consider_desc',
      keywords: [
        'review',
        'best seller',
        'compare',
        'deal',
        'features',
        'specifications',
        'recommend',
        'wishlist',
        'consider',
        'consideration',
      ],
    },
    {
      key: 'convert',
      label: 'Purchase',
      labelKey: 'funnel.ecommerce_convert_label',
      description: 'Add to cart & checkout',
      descriptionKey: 'funnel.ecommerce_convert_desc',
      keywords: [
        'buy',
        'shop now',
        'order',
        'sale',
        'discount',
        'limited time',
        'add to cart',
        'checkout',
        'purchase',
        'promo',
      ],
    },
  ],
  metrics: {
    primary: 'orders',
    secondary: 'add_to_carts',
  },
}

/**
 * Real estate funnel
 * Stages: Awareness → Consideration → Conversion (Leads)
 */
export const REAL_ESTATE_FUNNEL: FunnelConfig = {
  id: 'realestate',
  name: 'Real Estate Funnel',
  industry: 'realestate',
  stages: [
    {
      key: 'aware',
      label: 'Awareness',
      labelKey: 'funnel.realestate_aware_label',
      description: 'Property discovery',
      descriptionKey: 'funnel.realestate_aware_desc',
      keywords: [
        'listing',
        'property showcase',
        'new project',
        'launch',
        'development',
        'community',
      ],
    },
    {
      key: 'consider',
      label: 'Consideration',
      labelKey: 'funnel.realestate_consider_label',
      description: 'Property research',
      descriptionKey: 'funnel.realestate_consider_desc',
      keywords: [
        'virtual tour',
        'floor plan',
        'amenities',
        'location',
        'specifications',
        'brochure',
        'gallery',
        'consider',
        'consideration',
      ],
    },
    {
      key: 'convert',
      label: 'Conversion',
      labelKey: 'funnel.realestate_convert_label',
      description: 'Inquiries & visits',
      descriptionKey: 'funnel.realestate_convert_desc',
      keywords: [
        'inquire',
        'schedule visit',
        'register interest',
        'contact',
        'book viewing',
        'request info',
        'lead',
      ],
    },
  ],
  metrics: {
    primary: 'qualified_leads',
    secondary: 'inquiries',
  },
}

/**
 * Lead generation funnel (for automotive, banking, insurance)
 * Stages: Awareness → Consideration → Lead
 */
export const LEAD_GEN_FUNNEL: FunnelConfig = {
  id: 'lead_gen',
  name: 'Lead Generation Funnel',
  industry: 'lead_gen',
  stages: [
    {
      key: 'aware',
      label: 'Awareness',
      labelKey: 'funnel.lead_gen_aware_label',
      description: 'Brand & service discovery',
      descriptionKey: 'funnel.lead_gen_aware_desc',
      keywords: [
        'launch',
        'reveal',
        'showcase',
        'brand awareness',
        'new product',
        'service introduction',
        'promotion',
        'visibility',
      ],
    },
    {
      key: 'consider',
      label: 'Consideration',
      labelKey: 'funnel.lead_gen_consider_label',
      description: 'Research & comparison',
      descriptionKey: 'funnel.lead_gen_consider_desc',
      keywords: [
        'compare',
        'learn more',
        'features',
        'benefits',
        'review',
        'guide',
        'explainer',
        'brochure',
        'information',
        'details',
        'specifications',
        'consider',
        'consideration',
      ],
    },
    {
      key: 'convert',
      label: 'Lead',
      labelKey: 'funnel.lead_gen_convert_label',
      description: 'Lead form submissions & inquiries',
      descriptionKey: 'funnel.lead_gen_convert_desc',
      keywords: [
        'lead',
        'quote',
        'inquiry',
        'apply',
        'request',
        'contact',
        'sign up',
        'register',
        'book',
        'appointment',
        'consultation',
        'call back',
      ],
    },
  ],
  metrics: {
    primary: 'qualified_leads',
    secondary: 'inquiries',
  },
}

/**
 * Insurance funnel (legacy - for backward compatibility)
 * Stages: Awareness → Consideration → Policy
 */
export const INSURANCE_FUNNEL: FunnelConfig = {
  id: 'insurance',
  name: 'Insurance Funnel',
  industry: 'insurance',
  stages: [
    {
      key: 'aware',
      label: 'Awareness',
      labelKey: 'funnel.insurance_aware_label',
      description: 'Brand awareness campaigns',
      descriptionKey: 'funnel.insurance_aware_desc',
      keywords: ['awareness', 'brand', 'insurance introduction', 'protection'],
    },
    {
      key: 'consider',
      label: 'Consideration',
      labelKey: 'funnel.insurance_consider_label',
      description: 'Plan comparison & research',
      descriptionKey: 'funnel.insurance_consider_desc',
      keywords: [
        'compare',
        'guide',
        'learn',
        'review',
        'ประกันรถ',
        'ประกันสุขภาพ',
        'ประกันการเดินทาง',
        'health plan',
        'motor',
        'travel protection',
        'consider',
        'consideration',
      ],
    },
    {
      key: 'convert',
      label: 'Policy',
      labelKey: 'funnel.insurance_convert_label',
      description: 'Quote requests & policy purchases',
      descriptionKey: 'funnel.insurance_convert_desc',
      keywords: [
        'quote',
        'get quote',
        'policy',
        'renewal',
        'ซื้อ',
        'ต่อ',
        'apply',
        'purchase',
        'buy',
      ],
    },
  ],
  metrics: {
    primary: 'policies',
    secondary: 'quotes',
  },
}

// ============================================================================
// DEMO-CRITICAL INDUSTRY FUNNELS (Supplement, EV, FMCG)
// ============================================================================

/**
 * Supplement Brand funnel
 * Stages: Awareness → Consideration → Purchase
 * Key insight: supplement brands over-invest in awareness;
 * optimizer shifts budget to conversion + retention (repeat purchases).
 */
export const SUPPLEMENT_FUNNEL: FunnelConfig = {
  id: 'supplement',
  name: 'Supplement Brand Funnel',
  industry: 'supplement',
  stages: [
    {
      key: 'aware',
      label: 'Awareness',
      labelKey: 'funnel.supplement_aware_label',
      description: 'Brand & product discovery',
      descriptionKey: 'funnel.supplement_aware_desc',
      keywords: [
        'wellness',
        'vitamin',
        'supplement',
        'health',
        'immune',
        'beauty',
        'collagen',
        'brand launch',
        'new product',
        'อาหารเสริม',
        'วิตามิน',
        'คอลลาเจน',
      ],
    },
    {
      key: 'consider',
      label: 'Consideration',
      labelKey: 'funnel.supplement_consider_label',
      description: 'Product research & comparison',
      descriptionKey: 'funnel.supplement_consider_desc',
      keywords: [
        'review',
        'benefits',
        'ingredients',
        'compare',
        'best seller',
        'recommend',
        'doctor',
        'clinical',
        'results',
        'how to use',
        'guide',
        'สรรพคุณ',
        'รีวิว',
      ],
    },
    {
      key: 'convert',
      label: 'Purchase',
      labelKey: 'funnel.supplement_convert_label',
      description: 'Add to cart & checkout',
      descriptionKey: 'funnel.supplement_convert_desc',
      keywords: [
        'buy',
        'shop now',
        'order',
        'sale',
        'discount',
        'promo',
        'add to cart',
        'limited offer',
        'flash sale',
        'subscribe',
        'ซื้อ',
        'สั่ง',
        'โปร',
      ],
    },
  ],
  metrics: {
    primary: 'orders',
    secondary: 'add_to_carts',
  },
}

/**
 * EV (Electric Vehicle) Brand funnel
 * Stages: Awareness → Consideration → Conversion (Test Drive / Reservation)
 * Long consideration cycle; test drive is the key micro-conversion.
 */
export const EV_FUNNEL: FunnelConfig = {
  id: 'ev',
  name: 'EV Brand Funnel',
  industry: 'ev',
  stages: [
    {
      key: 'aware',
      label: 'Awareness',
      labelKey: 'funnel.ev_aware_label',
      description: 'EV model discovery & brand building',
      descriptionKey: 'funnel.ev_aware_desc',
      keywords: [
        'launch',
        'reveal',
        'electric vehicle',
        'EV',
        'new model',
        'range',
        'sustainable',
        'innovation',
        'brand awareness',
        'showcase',
        'รถยนต์ไฟฟ้า',
        'รถ EV',
      ],
    },
    {
      key: 'consider',
      label: 'Consideration',
      labelKey: 'funnel.ev_consider_label',
      description: 'Research, comparison & test drive booking',
      descriptionKey: 'funnel.ev_consider_desc',
      keywords: [
        'compare',
        'test drive',
        'specifications',
        'range test',
        'charging',
        'review',
        'features',
        'configurator',
        'brochure',
        'incentive',
        'tax benefit',
        'ทดลองขับ',
        'เปรียบเทียบ',
      ],
    },
    {
      key: 'convert',
      label: 'Conversion',
      labelKey: 'funnel.ev_convert_label',
      description: 'Test drive attendance & reservation',
      descriptionKey: 'funnel.ev_convert_desc',
      keywords: [
        'book',
        'reserve',
        'pre-order',
        'test drive booking',
        'appointment',
        'dealer',
        'showroom visit',
        'reservation',
        'deposit',
        'จอง',
        'นัดทดลองขับ',
        'วางมัดจำ',
      ],
    },
  ],
  metrics: {
    primary: 'reservations',
    secondary: 'test_drives',
  },
}

/**
 * FMCG (Fast-Moving Consumer Goods) funnel
 * Stages: Awareness → Consideration → Purchase
 * High-volume, low-margin, brand-driven.
 */
export const FMCG_FUNNEL: FunnelConfig = {
  id: 'fmcg',
  name: 'FMCG Funnel',
  industry: 'fmcg',
  stages: [
    {
      key: 'aware',
      label: 'Awareness',
      labelKey: 'funnel.fmcg_aware_label',
      description: 'Brand recall & product visibility',
      descriptionKey: 'funnel.fmcg_aware_desc',
      keywords: [
        'brand',
        'launch',
        'new product',
        'tvc',
        'sponsorship',
        'influencer',
        'trending',
        'viral',
        'reach',
        'awareness',
        'แบรนด์',
        'โฆษณา',
        'ใหม่',
      ],
    },
    {
      key: 'consider',
      label: 'Consideration',
      labelKey: 'funnel.fmcg_consider_label',
      description: 'Trial intent & purchase consideration',
      descriptionKey: 'funnel.fmcg_consider_desc',
      keywords: [
        'review',
        'recommend',
        'best seller',
        'compare',
        'deal',
        'offer',
        'promotion',
        'features',
        'benefits',
        'how to',
        'รีวิว',
        'แนะนำ',
        'โปรโมชั่น',
      ],
    },
    {
      key: 'convert',
      label: 'Purchase',
      labelKey: 'funnel.fmcg_convert_label',
      description: 'Online + offline purchase',
      descriptionKey: 'funnel.fmcg_convert_desc',
      keywords: [
        'buy',
        'shop now',
        'order',
        'sale',
        'discount',
        'add to cart',
        'flash sale',
        'cash on delivery',
        'store visit',
        'purchase',
        'ซื้อ',
        'สั่ง',
        'ลด',
      ],
    },
  ],
  metrics: {
    primary: 'orders',
    secondary: 'store_visits',
  },
}
